/**
 * TREATX-MANAGED-RUNTIME-CONTENT-01 — Super Admin → Content Management
 * --------------------------------------------------------------------
 * Fully data-driven: the list, the editable fields and the language tabs all
 * come from GET /api/admin/managed-content (catalogue + canonical locale
 * authority). No content key, locale or surface is hard-coded here, so a
 * future registered key appears without any frontend change.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, ExternalLink, Eye,
  Loader2, Search, ShieldAlert,
} from 'lucide-react';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { SafeHtml } from '../../components/security/SafeHtml';

const AUDIENCE_LABEL = {
  public: 'Public', patient: 'Patient', provider: 'Provider', admin: 'Admin',
  mixed: 'Mixed',
};

const CTX_LABEL = {
  direct: 'Open surface',
  requires_session: 'Requires patient/provider session',
  surface_not_linked: 'Surface not currently linked',
  not_practical: 'No directly openable surface',
};

/** A context is only openable when the catalogue gives a concrete route —
 *  parameterised patterns are never turned into links (no fabricated IDs). */
const openableRoute = (context) => {
  const route = context?.route;
  if (!route || route.includes(':') || context.availability === 'not_practical') return null;
  return route;
};

const Pill = ({ tone = 'slate', children, testid }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border bg-${tone}-50 text-${tone}-700 border-${tone}-200`}
    data-testid={testid}
  >
    {children}
  </span>
);

const Readiness = ({ readiness, testid }) => {
  const r = readiness || {};
  const enabledTotal = (r.enabled_locales || []).length;
  const enabledReady = (r.enabled_ready || []).length;
  const otherMissing = r.other_missing || [];
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className={`text-xs font-medium ${enabledReady === enabledTotal ? 'text-emerald-700' : 'text-amber-700'}`}>
        Enabled: {enabledReady}/{enabledTotal} ready
      </div>
      <div className="text-[11px] text-slate-500">
        {otherMissing.length === 0
          ? 'Other supported: all prepared'
          : `Other supported: ${otherMissing.map((c) => c.toUpperCase()).join(', ')} not yet prepared`}
      </div>
    </div>
  );
};

const localeMeta = (locales, code) =>
  (locales?.supported || []).find((l) => l.code === code) || { code, name: code, direction: 'ltr' };

const ContentEditor = ({ itemKey, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locale, setLocale] = useState(null);
  const [draft, setDraft] = useState({ body: '', title: '', summary: '' });
  const [advanced, setAdvanced] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/managed-content/${itemKey}`);
      setData(res.data);
      const first = locale || res.data?.locales?.default || 'en';
      setLocale(first);
      setDraft({
        body: res.data?.current?.body?.[first] || '',
        title: res.data?.current?.title?.[first] || '',
        summary: res.data?.current?.summary?.[first] || '',
      });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || 'Could not load content');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey]);

  useEffect(() => { load(); }, [load]);

  const selectLocale = (code) => {
    setLocale(code);
    setDraft({
      body: data?.current?.body?.[code] || '',
      title: data?.current?.title?.[code] || '',
      summary: data?.current?.summary?.[code] || '',
    });
  };

  const item = data?.item;
  const meta = localeMeta(data?.locales, locale);
  const isHtml = item?.format === 'html';
  const rtl = meta.direction === 'rtl';

  const publish = async () => {
    setSaving(true);
    setConfirmOpen(false);
    try {
      const res = await api.put(`/admin/managed-content/${itemKey}`, {
        locale,
        body: draft.body,
        ...(advanced ? { title: draft.title, summary: draft.summary } : {}),
      });
      toast.success(`Published v${res.data?.new_version} (${locale.toUpperCase()})`);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d || 'Publish failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !item) {
    return (
      <div className="py-12 flex items-center gap-2 text-slate-500" data-testid="content-editor-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const present = data.current?.body || {};
  const enabled = data.locales?.enabled || [];

  return (
    <div data-testid={`content-editor-${itemKey}`}>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4"
        data-testid="content-editor-back">
        <ArrowLeft className="h-4 w-4" /> All content
      </button>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{item.admin_label}</h2>
              <div className="text-xs text-slate-500 mt-1 font-mono" data-testid="editor-key">{item.key}</div>
              <p className="text-sm text-slate-600 mt-2 max-w-xl">{item.purpose}</p>
              <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-slate-600">
                <Pill testid="editor-status">{item.content_status || '—'} · v{item.current_version}</Pill>
                <Pill testid="editor-audience">{AUDIENCE_LABEL[item.audience] || item.audience}</Pill>
                {(item.usage || []).map((u) => (
                  <Pill key={u.surface_label} testid="editor-usage">{u.surface_label}</Pill>
                ))}
                {item.sensitive && (
                  <Pill tone="rose" testid="editor-sensitive">Evidence-sensitive</Pill>
                )}
              </div>
            </div>
            {openableRoute(item.context) ? (
              <div className="text-right">
                <Button variant="outline" size="sm" asChild data-testid="view-in-context-btn">
                  <a href={`#${openableRoute(item.context)}`} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> View in context
                  </a>
                </Button>
                <div className="text-[11px] text-slate-500 mt-1" data-testid="view-in-context-note">
                  {CTX_LABEL[item.context.availability]}
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 text-right max-w-[220px]"
                data-testid="view-in-context-unavailable">
                {item.context?.requires || CTX_LABEL[item.context?.availability] || 'No directly openable surface'}
              </div>
            )}
          </div>

          {/* Language tabs — all canonical supported locales */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="language-tabs">
            {(data.locales?.supported || []).map((l) => {
              const has = Boolean(present[l.code]);
              const isEnabled = enabled.includes(l.code);
              const active = l.code === locale;
              return (
                <button
                  key={l.code}
                  onClick={() => selectLocale(l.code)}
                  data-testid={`language-tab-${l.code}`}
                  data-enabled={isEnabled ? 'true' : 'false'}
                  data-available={has ? 'true' : 'false'}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    active ? 'bg-slate-900 text-white border-slate-900'
                      : has ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-white text-slate-500 border-slate-200'}`}
                >
                  {l.name}
                  <span className="ml-1 opacity-70">
                    {has ? 'Available' : 'Missing'}{isEnabled ? '' : ' · not enabled'}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-5">
            <label className="text-xs font-semibold text-slate-700">
              Runtime text — {meta.name}{rtl ? ' (right-to-left)' : ''}
            </label>
            <Textarea
              dir={rtl ? 'rtl' : 'ltr'}
              className={`mt-2 min-h-[180px] text-sm ${rtl ? 'text-right' : ''}`}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              data-testid="editor-body-input"
            />
            {isHtml && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                  <Eye className="h-3.5 w-3.5" /> Sanitised preview
                </div>
                <div dir={rtl ? 'rtl' : 'ltr'} className="rounded-lg border p-3 text-sm bg-slate-50"
                  data-testid="editor-html-preview">
                  <SafeHtml html={draft.body} />
                </div>
              </div>
            )}

            <button onClick={() => setAdvanced((v) => !v)}
              className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
              data-testid="editor-advanced-toggle">
              {advanced ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Advanced (title / summary — not consumed by runtime today)
            </button>
            {advanced && (
              <div className="mt-3 space-y-3">
                <Input dir={rtl ? 'rtl' : 'ltr'} placeholder="Title" value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  data-testid="editor-title-input" />
                <Textarea dir={rtl ? 'rtl' : 'ltr'} placeholder="Summary" value={draft.summary}
                  onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                  data-testid="editor-summary-input" />
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Button onClick={() => setConfirmOpen(true)} disabled={saving}
              data-testid="save-and-publish-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save &amp; Publish
            </Button>
            <Readiness readiness={item.readiness} testid="editor-readiness" />
          </div>

          {(item.readiness?.enabled_missing || []).length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
              data-testid="missing-translation-warning">
              Missing translations for enabled locales:{' '}
              {(item.readiness.enabled_missing).map((c) => c.toUpperCase()).join(', ')}. Publishing is
              still allowed — the runtime falls back to the controlling locale.
            </div>
          )}

          <div className="mt-6 border-t pt-4">
            <button onClick={() => setHistoryOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700"
              data-testid="version-history-toggle">
              {historyOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Version history ({(data.history || []).length})
            </button>
            {historyOpen && (
              <ul className="mt-2 space-y-1" data-testid="version-history-list">
                {(data.history || []).map((h) => (
                  <li key={h.version} className="text-xs text-slate-600">
                    <b>v{h.version}</b> · {h.published_at || '—'} · {h.published_by || '—'} ·{' '}
                    {(h.locales_present || []).map((c) => c.toUpperCase()).join(', ') || 'none'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="publish-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Publish {meta.name} text?</DialogTitle>
            <DialogDescription className="text-left">
              A new version is created. Every other language in this content stays exactly as it is,
              and all previous versions are preserved.
              {item.sensitive && (
                <span className="block mt-3 text-rose-700 font-medium" data-testid="sensitive-publish-warning">
                  <ShieldAlert className="h-3.5 w-3.5 inline mr-1" />
                  This is acceptance/evidence-sensitive content. Changing it affects the text shown
                  for future interactions. Historical evidence and earlier versions remain unchanged.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}
              data-testid="publish-cancel">Cancel</Button>
            <Button onClick={publish} disabled={saving} data-testid="publish-confirm">
              Save &amp; Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function ContentManagement() {
  const navigate = useNavigate();
  const { admin } = useTreatXAdminAuth();
  const isSuperAdmin = admin?.role === 'treatx_super_admin';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [audienceFilter, setAudienceFilter] = useState('ALL');
  const [missingOnly, setMissingOnly] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (admin && !isSuperAdmin) navigate('/treatx-admin/dashboard', { replace: true });
  }, [admin, isSuperAdmin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/managed-content');
      setData(res.data);
      setError(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || d || e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);

  const items = useMemo(() => {
    const rows = data?.items || [];
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'ALL' && (r.content_status || '') !== statusFilter) return false;
      if (audienceFilter !== 'ALL' && r.audience !== audienceFilter) return false;
      if (missingOnly && (r.readiness?.enabled_missing || []).length === 0) return false;
      if (!needle) return true;
      return r.key.toLowerCase().includes(needle)
        || (r.admin_label || '').toLowerCase().includes(needle)
        || (r.usage || []).some((u) => (u.surface_label || '').toLowerCase().includes(needle));
    });
  }, [data, q, statusFilter, audienceFilter, missingOnly]);

  if (!isSuperAdmin) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="content-management-page">
      <div className="max-w-6xl mx-auto px-4">
        <button onClick={() => navigate('/treatx-admin/dashboard')}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"
          data-testid="content-management-back">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <h1 className="text-2xl font-semibold text-slate-900">Content Management</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">
          Administratively managed runtime texts. Super Admin only.
        </p>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 mb-4"
            data-testid="content-management-error">{String(error)}</div>
        )}

        {selected ? (
          <ContentEditor itemKey={selected} onBack={() => { setSelected(null); load(); }} />
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                  placeholder="Search name, key or usage" data-testid="content-search" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="filter-status">
                <option value="ALL">All statuses</option>
                <option value="published">Published</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>
              <select value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)}
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                data-testid="filter-audience">
                <option value="ALL">All audiences</option>
                {['public', 'patient', 'provider', 'admin', 'mixed'].map((a) => (
                  <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>
                ))}
              </select>
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 px-2"
                data-testid="filter-missing-wrap">
                <input type="checkbox" checked={missingOnly}
                  onChange={(e) => setMissingOnly(e.target.checked)}
                  data-testid="filter-missing-translations" />
                Missing translations only
              </label>
            </div>

            {loading ? (
              <div className="py-12 flex items-center gap-2 text-slate-500" data-testid="content-list-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block bg-white border rounded-xl overflow-hidden">
                  <table className="w-full text-sm" data-testid="content-table">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2">Content name</th>
                        <th className="px-4 py-2">Key</th>
                        <th className="px-4 py-2">Used in</th>
                        <th className="px-4 py-2">Audience</th>
                        <th className="px-4 py-2">Language readiness</th>
                        <th className="px-4 py-2">Status</th>
                        <th className="px-4 py-2">Last updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((r) => (
                        <tr key={r.key} className="border-t hover:bg-slate-50 cursor-pointer"
                          onClick={() => setSelected(r.key)} data-testid={`content-row-${r.key}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{r.admin_label}</div>
                            <div className="flex gap-1 mt-1">
                              {r.sensitive && <Pill tone="rose" testid={`row-sensitive-${r.key}`}>Evidence-sensitive</Pill>}
                              {r.context?.availability === 'surface_not_linked' && (
                                <Pill tone="amber" testid={`row-unlinked-${r.key}`}>Surface not currently linked</Pill>
                              )}
                              {r.status === 'retired' && <Pill testid={`row-retired-${r.key}`}>Retired</Pill>}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.key}</td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {(r.usage || []).map((u) => u.surface_label).join(', ') || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs">{AUDIENCE_LABEL[r.audience] || r.audience}</td>
                          <td className="px-4 py-3"><Readiness readiness={r.readiness} testid={`row-readiness-${r.key}`} /></td>
                          <td className="px-4 py-3 text-xs">{r.content_status || 'missing'}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{r.updated_at || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3" data-testid="content-cards">
                  {items.map((r) => (
                    <button key={r.key} onClick={() => setSelected(r.key)}
                      className="w-full text-left bg-white border rounded-xl p-4"
                      data-testid={`content-card-${r.key}`}>
                      <div className="font-medium text-slate-900">{r.admin_label}</div>
                      <div className="font-mono text-[11px] text-slate-500 mt-0.5 break-all">{r.key}</div>
                      <div className="text-xs text-slate-600 mt-2">
                        {(r.usage || []).map((u) => u.surface_label).join(', ') || '—'}
                      </div>
                      <div className="mt-2"><Readiness readiness={r.readiness} /></div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Pill>{AUDIENCE_LABEL[r.audience] || r.audience}</Pill>
                        <Pill>{r.content_status || 'missing'}</Pill>
                        {r.sensitive && <Pill tone="rose">Evidence-sensitive</Pill>}
                      </div>
                    </button>
                  ))}
                </div>

                {items.length === 0 && (
                  <div className="py-10 text-center text-sm text-slate-500" data-testid="content-empty">
                    No managed content matches these filters.
                  </div>
                )}

                <div className="mt-4 flex items-start gap-2 text-[11px] text-slate-500">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                  Content keys cannot be created, renamed or deleted here. New runtime content becomes
                  manageable once development registers it in the managed-content catalogue.
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
